import { Dispatch, SetStateAction } from "react";
import { useStoreWrapper } from "../../../../../store/store";
import { toast } from "react-toastify";
import Modal from "../../../../../components/Modal";
import { AssModal } from "../../components/Roles/style";
import { AiOutlineClose } from "react-icons/ai";
import {
  convertObjEntriesToArray,
  toastConfig,
  valueChecker,
} from "../../../../../utils/helpers";
import AccordionInput from "../../components/Roles/AccordionInput";
import Button from "../../../../../components/Button";
import { ColumnContainer, MobileHeading, RowData } from "./style";
import { PERMISSION_LIST } from "../../../../../shared/sharedConstants";

// These are the Profile types that do not require an email address to be
// associated with the user
export const noEmailArr = ["Guest User", "Incident Entry", "Supervisor"];

export function useStoreWrapperHook() {
  const { selectedUser, setEdit, edit, user, userFacilities } = useStoreWrapper(
    (state) => ({
      selectedUser: state.selectedUser,
      selectedUserFacilities: state.selectedUserFacilities,
      setEdit: state.setEdit,
      edit: state.edit,
      user: state.user,
      userFacilities: state.userFacilities,
    })
  );

  return { selectedUser, setEdit, edit, user, userFacilities };
}

interface UpdateRowParams {
  setTableData: Dispatch<SetStateAction<any[]>>;
  setProfile: Dispatch<SetStateAction<any>>;
  setFacility: Dispatch<SetStateAction<any>>;
  setRole: Dispatch<SetStateAction<any>>;
  setAssignmentPermissions: Dispatch<SetStateAction<any>>;
  getPermissionObject: (
    permissions: any,
    setPermissionList: Dispatch<SetStateAction<any>>
  ) => void;
  refetchFacilities: () => void;
  refetchProfiles: () => void;
  setPermissionList: Dispatch<SetStateAction<any>>;
}

export function updateRow(
  item: any,
  index: number,
  {
    setTableData,
    setProfile,
    setFacility,
    setRole,
    setAssignmentPermissions,
    getPermissionObject,
    refetchFacilities,
    refetchProfiles,
    setPermissionList,
  }: UpdateRowParams
) {
  setTableData((old: any) =>
    old.map((row: any, ind: number) => {
      if (!old[index].edit) {
        const permissionListValue = {
          ...old[index]?.assignment?.permissions,
        };
        setProfile({ ...old[index].profile });
        setFacility({ ...old[index].facility });
        setRole({ ...old[index].role });
        setAssignmentPermissions(permissionListValue);

        getPermissionObject({ ...permissionListValue }, setPermissionList);
        refetchFacilities();
        refetchProfiles();
      }

      if (ind === index) {
        return {
          ...old[index],
          edit: !old[index].edit,
        };
      }
      return row;
    })
  );
}

interface AssignmentTemplateParams {
  setIdToDelete: Dispatch<SetStateAction<string>>;
  setOpenDelete: Dispatch<SetStateAction<boolean>>;
  user: any;
  updateFacilityData: any;
  setEdit: any;
  resetStates: () => void;
}

export function getAssignmentTemplate({
  setIdToDelete,
  setOpenDelete,
  user,
  updateFacilityData,
  setEdit,
  resetStates,
}: AssignmentTemplateParams) {
  return {
    assignment: null,
    edit: false,
    new: true,
    facility: {},
    facilityId: "",
    profile: {},
    role: {},
    handleDeleteUserAssignment: (id: string) => {
      setIdToDelete(id);
      setOpenDelete(true);
    },
    handleEditUserAssignment: (assignment: any, current: any) => {
      doHandleEditAssignment({
        assignment,
        current,
        user,
        updateFacilityData,
        setEdit,
        reset: resetStates,
      });
    },
  };
}

interface DoHandleEditAssignmentParams {
  assignment: any;
  current: any;
  user: any;
  updateFacilityData: any;
  setEdit: (status: boolean) => void;
  reset: () => void;
  cb?: () => void;
}

export async function doHandleEditAssignment({
  assignment,
  current,
  user,
  updateFacilityData,
  setEdit,
  reset,
  cb,
}: DoHandleEditAssignmentParams) {
  const _current = {
    ...current,
  };
  delete _current.handleDeleteAssignment;
  delete _current.handleEditAssignment;

  try {
    setEdit(true);
    const currAssignment = { ...assignment };
    const { userId: loggedInUserId } = user;
    const request = {
      loggedInUserId,
      ...currAssignment.permissions,
      roleId: currAssignment.role.roleId,
      profileId: currAssignment.profile.profileId,
      assignmentId: currAssignment.assignmentId,
    };

    const res = await updateFacilityData.mutateAsync({ ...request });
    reset();

    // ------- check if callback is present -----
    if (cb) {
      cb();
    }

    toast.success(res.data?.message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 2000,
      toastId: "1",
    });
  } catch (error: any) {
    toast.error(error?.response?.data?.message ?? error?.message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 2000,
      toastId: "1",
    });
  } finally {
    setEdit(false);
  }
}

interface DoHandleDeleteAssignmentParams {
  loggedInUserId: string;
  assignmentId: string;
  deleteAssignment: (data: {
    loggedInUserId: string;
    assignmentId: string;
  }) => Promise<any>;
  setOpenDelete: (status: boolean) => void;
  cb?: (facilities: any) => void;
}

export async function doHandleDeleteAssignment({
  loggedInUserId,
  assignmentId,
  deleteAssignment,
  setOpenDelete,
  cb,
}: DoHandleDeleteAssignmentParams) {
  const data = {
    loggedInUserId,
    assignmentId,
  };
  try {
    const res = await deleteAssignment(data);
    if (cb) cb(res.data.data.facilities);
    toast.success(res.data.message || "User assignment deleted successfully", {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 2000,
      toastId: "1",
    });
    setOpenDelete(false);
  } catch (err: any) {
    console.log({ err });
    toast.error(err.response?.data?.message || "Delete action failed!", {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 2000,
      toastId: "1",
    });
  }
}

interface PermissionModalProps {
  open: boolean;
  close: () => void;
  modalTitle: string;
  assignmentPermissions: any;
  setPermissionList: (permissions: any) => void;
}

export function PermissionModal({
  open,
  close,
  modalTitle,
  assignmentPermissions,
  setPermissionList,
}: Readonly<PermissionModalProps>) {
  return (
    <Modal open={open} close={close}>
      <AssModal>
        <button className="close-icon border-0 bg-transparent" onClick={close}>
          <AiOutlineClose />
        </button>
        <span className="mt-4 block text-center font-inter text-xl font-medium text-purple-600">
          Set Permissions for {modalTitle}
        </span>

        {/* ------- permission list ------- */}
        <div className="mt-8">
          {convertObjEntriesToArray(assignmentPermissions).map(
            (ele: any, ind) => (
              <div key={ele[ind]} className="mb-4">
                <AccordionInput
                  ele={ele}
                  setPermissionList={setPermissionList}
                />
              </div>
            )
          )}
          <Button
            data-testid="continue-assigment"
            addClass="w-full bg-purple-light text-white-main"
            click={close}
          >
            Continue
          </Button>
        </div>
      </AssModal>
    </Modal>
  );
}

interface CreateNewAssignmentParams {
  user: any;
  selectedUser: any;
  profile: any;
  role: any;
  facility: any;
  permissionList: any;
  addNewAssignment: (request: any) => Promise<any>;
  resetStates: () => void;
  refetchAss: () => void;
  setTableData: (data: any) => void;
  setNewAss: (status: boolean) => void;
  from: string;
}

export async function createNewAssignment({
  user,
  selectedUser,
  profile,
  role,
  facility,
  permissionList,
  addNewAssignment,
  resetStates,
  refetchAss,
  setTableData,
  setNewAss,
  from,
}: CreateNewAssignmentParams) {
  try {
    setNewAss(true);
    const { userId: loggedInUserId } = user;
    const { userId } = from === "settings" ? user : selectedUser;

    // ------ configure assignment request ------
    const request = {
      loggedInUserId,
      userId,
      profileId: profile?.profileId ?? "",
      roleId: role?.roleId ?? "",
      facilityId: facility?.facilityId ?? "",
      ...permissionList,
    };
    const res = await addNewAssignment(request);
    toast.success(res?.data?.message, toastConfig(toast));

    // ------ reset state datas ------
    resetStates();
    refetchAss();

    setTableData((data: any) => {
      const newValue = [...data];
      newValue.pop();
      return newValue;
    });
  } catch (error: any) {
    toast.error(
      error?.response?.data?.message ?? error?.message,
      toastConfig(toast)
    );
  }
}

interface HandleProfileOnChangeParams {
  val: any;
  prevVal: any;
  selectedUser: any;
  tableData: any[];
  setProfile: (profile: any) => void;
  setRole: (role: any) => void;
  setAssignmentPermissions: (permissions: any) => void;
  getPermissionObject: (
    permissions: any,
    setPermissionList: (permissions: any) => void
  ) => void;
  setPermissionList: (permissions: any) => void;
}

export function handleProfileOnChange({
  val,
  prevVal,
  selectedUser,
  tableData,
  setProfile,
  setRole,
  setAssignmentPermissions,
  getPermissionObject,
  setPermissionList,
}: HandleProfileOnChangeParams) {
  if (
    prevVal &&
    noEmailArr.includes(prevVal.profileName) &&
    !selectedUser?.email &&
    !noEmailArr.includes(val.profileName)
  ) {
    toast.error(
      "Add an email address to user profile tab before changing profile assignment"
    );
    return;
  }

  // Check if tableData has any entry with new: false
  const hasUnnewEntries = tableData.some((entry: any) => entry.new === false);
  if (hasUnnewEntries) {
    // Further check within the assignments of unnew entries
    const conflictingAssignment = tableData.some((entry: any) => {
      return (
        entry.assignment &&
        noEmailArr.includes(entry.assignment.profileName) &&
        !noEmailArr.includes(val.profileName)
      );
    });

    if (conflictingAssignment && !selectedUser?.email) {
      toast.error(
        `Cannot add a profile requiring an email. Add an email address to user profile tab before changing profile assignment`
      );
      return;
    }
  }

  setProfile(val);
  setRole(null);
  setAssignmentPermissions(val.permissions);
  getPermissionObject(val.permissions, setPermissionList);
}

interface OnEditClickParams {
  obj: any;
  assignment: any;
  current: any;
  user: any;
  selectedUser: any;
  profile: any;
  role: any;
  facility: any;
  permissionList: any;
  addNewAssignment: (request: any) => Promise<any>;
  resetStates: () => void;
  refetchAss: () => void;
  setTableData: (data: any) => void;
  setNewAss: (status: boolean) => void;
  from: string;
  newAss: boolean;
}

export function onEditClick({
  obj,
  assignment,
  current,
  user,
  selectedUser,
  profile,
  role,
  facility,
  permissionList,
  addNewAssignment,
  resetStates,
  refetchAss,
  setTableData,
  setNewAss,
  from,
  newAss,
}: OnEditClickParams) {
  // ----- add new assignment for user -------
  if (obj.new) {
    createNewAssignment({
      user,
      selectedUser,
      profile,
      role,
      facility,
      permissionList,
      addNewAssignment,
      resetStates,
      refetchAss,
      setTableData,
      setNewAss,
      from,
    });
    return;
  }

  if (newAss) {
    return;
  }

  if (obj.edit) {
    obj.handleEditUserAssignment(
      {
        ...assignment,
        permissions: permissionList,
        role,
        profile,
        facility,
      },
      current
    );
  }
}

interface OnCancelClickParams {
  obj: any;
  doCancelAddAssignment: () => void;
  refetchAss: () => void;
}

export function onCancelClick({
  obj,
  doCancelAddAssignment,
  refetchAss,
}: OnCancelClickParams) {
  if (obj.new) {
    doCancelAddAssignment();
  }
  if (obj.edit) {
    refetchAss();
  }
}

interface OnDeleteClickParams {
  obj: any;
  id: string;
  setTableData: (data: any) => void;
  resetStates: () => void;
  refetchAss: () => void;
}

export function onDeleteClick({
  obj,
  id,
  setTableData,
  resetStates,
  refetchAss,
}: OnDeleteClickParams) {
  if (obj.new) {
    setTableData((data: any) => {
      const newValue = [...data];
      newValue.pop();

      return newValue;
    });
    resetStates();
    refetchAss();
    return;
  }
  obj.handleDeleteUserAssignment(id);
}

interface HandleAssignmentDataParams {
  assData: any;
  assSuccess: boolean;
  user: any;
  updateFacilityData: any;
  setEdit: any;
  resetStates: () => void;
  refetchAss: () => void;
  setIdToDelete: Dispatch<SetStateAction<string>>;
  setOpenDelete: Dispatch<SetStateAction<boolean>>;
  setTableData: Dispatch<SetStateAction<any[]>>;
}

export function handleAssignmentData({
  assData,
  assSuccess,
  user,
  updateFacilityData,
  setEdit,
  resetStates,
  refetchAss,
  setIdToDelete,
  setOpenDelete,
  setTableData,
}: HandleAssignmentDataParams) {
  if (assSuccess) {
    const {
      data: {
        data: { facilities: selectedUserFacilities },
      },
    } = assData;
    const facilitiesData = selectedUserFacilities
      .sort((a: any, b: any) => (a.facilityName > b.facilityName ? 1 : -1))
      .map((facility: any) => {
        return {
          facility: {
            facilityName: valueChecker(facility.facilityName, "No Name"),
            facilityId: valueChecker(facility.facilityId, ""),
          },
          role: {
            roleName: valueChecker(facility.roleName, "No role assigned"),
            roleId: valueChecker(facility.roleId, ""),
          },
          profile: {
            profileName: valueChecker(facility.profileName, "No profile"),
            profileId: valueChecker(facility.profileId, ""),
          },
          facilityId: facility.facilityId,
          assignment: { ...facility },
          edit: false,
          new: false,
          handleDeleteUserAssignment: async (id: string) => {
            setIdToDelete(id);
            setOpenDelete(true);
          },
          handleEditUserAssignment: (assignment: any, current: any) => {
            doHandleEditAssignment({
              assignment,
              current,
              user,
              updateFacilityData,
              setEdit,
              reset: resetStates,
              cb: refetchAss,
            });
          },
        };
      });

    setTableData(facilitiesData);
  }
}

export function ColumnContent({ obj, children, content, heading }: any) {
  return (
    <ColumnContainer>
      <MobileHeading>{heading}</MobileHeading>
      {obj.edit || obj.new ? (
        children
      ) : (
        <RowData style={{ fontSize: "14px" }}>{content}</RowData>
      )}
    </ColumnContainer>
  );
}

export const checkDisableBtn = ({
  obj,
  from,
  isPermitted,
  profile,
  role,
}: any) => {
  if (!obj) {
    return false;
  }
  if (obj.new) return false;
  // If editing an existing assignment ensure profile and role are selected
  else if (obj.edit && (!profile || !role)) return true;
  else if (from === "settings" && obj.profile.profileName === "Guest User")
    return true;
  else return !isPermitted(PERMISSION_LIST.ASSIGN_PERMISSIONS, obj.facilityId);
};

// The activate/deactivate toggle acts on the store's selectedUser, which only
// makes sense under user management, not on the signed-in user's own Settings.
export const showMakeUserActiveOrInactive = (pathname: string) =>
  !pathname.includes("settings");

export const checkDeleteDisabled = ({
  obj,
  isPermitted,
  selectedUser,
}: any) => {
  if (obj?.new) {
    return false;
  } else {
    const hasPermission = isPermitted(
      PERMISSION_LIST.ASSIGN_PERMISSIONS,
      obj?.facilityId
    );
    if (!hasPermission || selectedUser) {
      return selectedUser ? selectedUser?.status !== "Active" : false;
    } else {
      return false;
    }
  }
};
